import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { env } from '../config/env';
import { cartService } from '../services/cart/cartService';
import { canAddToCart, getUnitPrice } from '../utils/product';
import { cartItemsToUpdateRequest, serverCartToItems } from '../utils/cartSync';
import type { CartContextType, CartItem, Product } from '../types';
import { useAuth } from './AuthContext';

const CartContext = createContext<CartContextType | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const skipNextSyncRef = useRef(false);
  const syncTimerRef = useRef<number | null>(null);

  const syncToServer = useCallback(
    (nextItems: CartItem[]) => {
      if (env.useMock || !isAuthenticated) return;

      if (syncTimerRef.current) {
        window.clearTimeout(syncTimerRef.current);
      }

      syncTimerRef.current = window.setTimeout(() => {
        void cartService.updateCart(cartItemsToUpdateRequest(nextItems)).catch(() => {});
      }, 400);
    },
    [isAuthenticated],
  );

  const commitItems = useCallback(
    (updater: (prev: CartItem[]) => CartItem[]) => {
      setItems((prev) => {
        const next = updater(prev);
        if (skipNextSyncRef.current) {
          skipNextSyncRef.current = false;
        } else {
          syncToServer(next);
        }
        return next;
      });
    },
    [syncToServer],
  );

  useEffect(() => {
    if (env.useMock || !isAuthenticated) return;

    let cancelled = false;

    void (async () => {
      try {
        const cart = await cartService.fetchCart();
        if (cancelled) return;
        skipNextSyncRef.current = true;
        setItems(serverCartToItems(cart.items));
      } catch {
        // Sepet yüklenemezse yerel durum korunur
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    return () => {
      if (syncTimerRef.current) {
        window.clearTimeout(syncTimerRef.current);
      }
    };
  }, []);

  const addToCart = (product: Product, quantity = 1) => {
    if (!canAddToCart(product)) return;

    commitItems((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      const currentQty = existing?.quantity ?? 0;
      const available = product.stock - currentQty;

      if (available <= 0) return prev;

      const addQty = Math.min(quantity, available);

      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + addQty, product }
            : item,
        );
      }

      return [...prev, { product, quantity: addQty }];
    });
  };

  const removeFromCart = (productId: string) => {
    commitItems((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    commitItems((prev) =>
      prev.map((item) => {
        if (item.product.id !== productId) return item;
        const capped = Math.min(quantity, item.product.stock);
        return { ...item, quantity: capped };
      }),
    );
  };

  const clearCart = useCallback((options?: { syncServer?: boolean }) => {
    skipNextSyncRef.current = true;
    setItems([]);
    const syncServer = options?.syncServer ?? true;
    if (syncServer && !env.useMock && isAuthenticated) {
      void cartService.clearCart().catch(() => {});
    }
  }, [isAuthenticated]);

  const totalItems = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity, 0),
    [items],
  );

  const totalPrice = useMemo(
    () => items.reduce((sum, item) => sum + getUnitPrice(item.product) * item.quantity, 0),
    [items],
  );

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}
